/**
 * Map Configuration Module
 *
 * Contains all map-related configuration including Mapbox settings,
 * default map properties, and control configurations.
 */

/**
 * Mapbox configuration settings
 * @typedef {Object} MapboxConfig
 * @property {string} accessToken - Mapbox access token
 * @property {string} style - Mapbox style URL
 */

/**
 * Map default settings
 * @typedef {Object} MapDefaults
 * @property {number} zoom - Default zoom level
 * @property {Array<number>} center - Default center coordinates [longitude, latitude]
 * @property {number} pitch - Default map pitch in degrees
 * @property {number} bearing - Default map bearing in degrees
 * @property {number} minZoom - Minimum zoom level allowed
 * @property {number} maxZoom - Maximum zoom level allowed
 * @property {string} projection - Map projection type
 */

/**
 * Map controls configuration
 * @typedef {Object} MapControls
 * @property {boolean} navigation - Enable navigation controls
 * @property {boolean} geolocate - Enable geolocation control
 * @property {boolean} fullscreen - Enable fullscreen control
 * @property {boolean} scale - Enable scale control
 */

/**
 * Parses center coordinates from environment variable
 * @returns {Array<number>} Center coordinates [longitude, latitude]
 */
function parseMapCenter() {
  const centerEnv = import.meta.env.VITE_DEFAULT_MAP_CENTER;
  if (centerEnv) {
    try {
      return JSON.parse(centerEnv);
    } catch (error) {
      console.warn('Failed to parse VITE_DEFAULT_MAP_CENTER, using default:', error);
    }
  }
  // Default to America center
  return [-100, 45];
}

/**
 * Map configuration object
 * Contains all map-related settings organized by category
 */
export const mapConfig = {
  /**
   * Mapbox service configuration
   * @type {MapboxConfig}
   */
  mapbox: {
    accessToken: import.meta.env.VITE_MAPBOX_TOKEN,
    style: 'mapbox://styles/mapbox/light-v10'
  },

  /**
   * Default map view settings
   * @type {MapDefaults}
   */
  defaults: {
    zoom: parseInt(import.meta.env.VITE_DEFAULT_MAP_ZOOM) || 3,
    center: parseMapCenter(),
    pitch: 0,
    bearing: 0,
    minZoom: parseFloat(import.meta.env.VITE_MAP_MIN_ZOOM) || 1.5,
    maxZoom: parseFloat(import.meta.env.VITE_MAP_MAX_ZOOM) || 18,
    projection: 'globe'
  },

  /**
   * Map controls configuration
   * @type {MapControls}
   */
  controls: {
    navigation: false,
    geolocate: false,
    fullscreen: false,
    scale: false
  },

  /**
   * Map interaction settings
   */
  interactions: {
    dragRotate: true,
    touchZoomRotate: true,
    doubleClickZoom: true,
    scrollZoom: true
  },

  /**
   * Performance and rendering settings
   */
  performance: {
    antialias: true,
    preserveDrawingBuffer: false,
    failIfMajorPerformanceCaveat: false
  }
};

/**
 * Validates that the Mapbox access token is available
 * @throws {Error} If Mapbox token is missing
 */
export function validateMapConfig() {
  if (!mapConfig.mapbox.accessToken) {
    throw new Error('Missing VITE_MAPBOX_TOKEN environment variable');
  }

  // Validate token format (basic check)
  if (!mapConfig.mapbox.accessToken.startsWith('pk.')) {
    console.warn('Mapbox token does not appear to be a valid public token');
  }
}

/**
 * Gets the map configuration for initialization
 * @returns {Object} Complete map configuration object
 */
export function getMapInitConfig() {
  return {
    container: undefined, // Will be set by the map component
    style: mapConfig.mapbox.style,
    center: mapConfig.defaults.center,
    zoom: mapConfig.defaults.zoom,
    pitch: mapConfig.defaults.pitch,
    bearing: mapConfig.defaults.bearing,
    minZoom: mapConfig.defaults.minZoom,
    maxZoom: mapConfig.defaults.maxZoom,
    projection: mapConfig.defaults.projection,
    antialias: mapConfig.performance.antialias,
    preserveDrawingBuffer: mapConfig.performance.preserveDrawingBuffer,
    failIfMajorPerformanceCaveat: mapConfig.performance.failIfMajorPerformanceCaveat,
    accessToken: mapConfig.mapbox.accessToken
  };
}

/**
 * Gets the map bounds for a given region
 * @param {string} region - Region identifier
 * @returns {Array<Array<number>>|null} Bounds array [[sw], [ne]] or null
 */
export function getRegionBounds(region) {
  const regions = {
    'north-america': [[-168.0, 7.0], [-50.0, 83.0]],
    'south-america': [[-92.0, -56.0], [-32.0, 15.0]],
    'europe': [[-31.0, 27.0], [70.0, 81.0]],
    'africa': [[-25.0, -35.0], [63.0, 38.0]],
    'asia': [[-180.0, -11.0], [180.0, 81.0]],
    'oceania': [[-180.0, -47.0], [180.0, 30.0]]
  };

  return regions[region] || null;
}